import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Injector,
  OnInit,
  Output,
  ViewContainerRef,
} from '@angular/core';

import { takeUntil } from 'rxjs/operators';

import { I18nService } from '@recruitee/i18n';
import {
  ConnectionPositionPair,
  DialogService,
  DialogWidth,
  IconName,
} from '@recruitee/user-interface';

import { WysiwygAction, WysiwygEmbeddableComponent } from '../adapter/wysiwyg-adapter.types';
import { RedactorInsertsDialogComponent } from './redactor-inserts-dialog/redactor-inserts-dialog.component';
import { WysiwygLink, WysiwygLinkType } from './wysiwyg-link.types';

export enum ActionType {
  LinkDelete = 'LinkDelete',
  LinkEdit = 'LinkEdit',
}

export type WysiwygLinkDeleteAction = WysiwygAction<ActionType.LinkDelete, WysiwygLink>;
export type WysiwygLinkEditAction = WysiwygAction<ActionType.LinkEdit, WysiwygLink>;

export type WysiwygLinkAction = WysiwygLinkDeleteAction | WysiwygLinkEditAction;

@Component({
  selector: 'rt-wysiwyg-link',
  templateUrl: './wysiwyg-link.component.html',
  styleUrls: ['./wysiwyg-link.component.less'],
})
export class WysiwygLinkComponent
  extends WysiwygEmbeddableComponent<WysiwygLink, WysiwygLinkAction>
  implements OnInit
{
  @Output() public closeDropdown: EventEmitter<void> = new EventEmitter();

  public dropdownOpened: boolean = false;

  public readonly icons: { [key in WysiwygLinkType]?: IconName } = {
    [WysiwygLinkType.Scheduler]: 'access-time',
    [WysiwygLinkType.GdprChange]: 'gdpr-candidate',
    [WysiwygLinkType.GdprConsentRequest]: 'gdpr-candidate',
    [WysiwygLinkType.GdprRemove]: 'gdpr-candidate',
    [WysiwygLinkType.Questionnaires]: 'list',
    [WysiwygLinkType.IntegrationsKununuApplicantFeedback]: 'rti-kununu',
    [WysiwygLinkType.IntegrationsKununuEmployeeFeedback]: 'rti-kununu',
  };

  public dropdownPositions: ConnectionPositionPair[] = [
    new ConnectionPositionPair(
      { originX: 'start', originY: 'bottom' },
      { overlayX: 'start', overlayY: 'top' },
    ),
    new ConnectionPositionPair(
      { originX: 'start', originY: 'top' },
      { overlayX: 'start', overlayY: 'bottom' },
    ),
  ];

  public texts: {
    dropdownTitle: string | undefined;
    linkName: string | undefined;
    label?: string;
    isValidLink: boolean;
  } = {
    dropdownTitle: undefined,
    linkName: undefined,
    isValidLink: false,
  };

  constructor(
    private dialogService: DialogService,
    private vcRef: ViewContainerRef,
    protected injector: Injector,
    protected cdRef: ChangeDetectorRef,
    private i18n: I18nService,
  ) {
    super(injector);
  }

  public edit(link: WysiwygLink): void {
    this.openModal(link);
  }

  public openModal(link: WysiwygLink) {
    const dialog = this.dialogService.openCustom(
      RedactorInsertsDialogComponent,
      { width: DialogWidth.Small },
      {
        type: link.type!,
        link: link.url!,
        label: link.label!,
        isEdit: true,
        expirationDays: +link.id!,
      },
      this.vcRef,
    );
    dialog.afterClosed.pipe(takeUntil(this.ngOnDestroy$.asObservable())).subscribe(opts => {
      if (opts) {
        if (opts.delete) {
          this.emit(new WysiwygAction(ActionType.LinkDelete, link));
        } else {
          this.emit(
            new WysiwygAction(ActionType.LinkEdit, {
              ...link,
              url: opts.link,
              label: opts.label
                ? opts.label
                : link.type !== WysiwygLinkType.Scheduler &&
                  link.type !== WysiwygLinkType.Questionnaires
                ? link.name
                : opts.link,
            }),
          );
        }
      }
      this.closeDropdown.emit();
    });
  }

  public remove(link: WysiwygLink): void {
    this.emit(new WysiwygAction(ActionType.LinkDelete, link));
  }

  public isPlaceholder(link: string | undefined): boolean {
    if (link) return /^\[.*]/.test(link);
    return false;
  }
  // eslint-disable-next-line max-lines-per-function
  protected onStateChange(nextState: WysiwygLink, prevState: WysiwygLink): void {
    // eslint-disable-next-line default-case
    switch (nextState.type) {
      case WysiwygLinkType.Scheduler:
        this.texts = {
          dropdownTitle: this.i18n.translate('app.wysiwyg.context_menu.scheduler'),
          linkName: this.i18n.translate('app.wysiwyg.link.scheduler_link_name'),
          label: nextState.label,
          isValidLink: !this.isPlaceholder(nextState.url),
        };
        break;

      case WysiwygLinkType.Questionnaires:
        this.texts = {
          dropdownTitle: this.i18n.translate('app.wysiwyg.context_menu.questionnaires'),
          linkName: nextState.label,
          label: nextState.label,
          isValidLink: !this.isPlaceholder(nextState.url),
        };
        break;

      case WysiwygLinkType.GdprConsentRequest:
        this.texts = {
          dropdownTitle: this.i18n.translate('app.wysiwyg.context_menu.gdpr'),
          linkName: nextState.label,
          label: nextState.label,
          isValidLink: !this.isPlaceholder(nextState.url),
        };
        break;
      case WysiwygLinkType.GdprChange:
        this.texts = {
          dropdownTitle: this.i18n.translate('app.wysiwyg.context_menu.gdpr'),
          linkName: nextState.label,
          label: nextState.label,
          isValidLink: !this.isPlaceholder(nextState.url),
        };
        break;
      case WysiwygLinkType.GdprRemove:
        this.texts = {
          dropdownTitle: this.i18n.translate('app.wysiwyg.context_menu.gdpr'),
          linkName: nextState.label,
          label: nextState.label,
          isValidLink: !this.isPlaceholder(nextState.url),
        };
        break;

      case WysiwygLinkType.IntegrationsKununuApplicantFeedback:
        this.texts = {
          dropdownTitle: this.i18n.translate('app.wysiwyg.context_menu.kununu'),
          linkName: this.i18n.translate(
            'app.wysiwyg.link.integrations_kununu_applicant_feedback_link_name',
          ),
          label: nextState.label,
          isValidLink: !this.isPlaceholder(nextState.url),
        };
        break;

      case WysiwygLinkType.IntegrationsKununuEmployeeFeedback:
        this.texts = {
          dropdownTitle: this.i18n.translate('app.wysiwyg.context_menu.kununu'),
          linkName: this.i18n.translate(
            'app.wysiwyg.link.integrations_kununu_employee_feedback_link_name',
          ),
          label: nextState.label,
          isValidLink: !this.isPlaceholder(nextState.url),
        };
        break;
    }

    super.onStateChange(nextState, prevState);
  }
}
